// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as BackgroundImageDB from '@app/features/theme/utils/BackgroundImageDB';
import VoiceSettings, {BLUR_BACKGROUND_ID, NONE_BACKGROUND_ID} from '@app/features/voice/state/VoiceSettings';
import {
	type CameraVideoProcessorOptions,
	createCameraVideoProcessor,
} from '@app/features/voice/utils/CameraVideoProcessor';
import type {BackgroundProcessorOptions as LiveKitBackgroundProcessorOptions} from '@livekit/track-processors';
import type {LocalVideoTrack} from 'livekit-client';

const logger = new Logger('VideoBackgroundProcessor');
const MEDIAPIPE_TASKS_VISION_PATH = '/libs/mediapipe/tasks-vision/0.10.14/wasm';
const MEDIAPIPE_SEGMENTER_MODEL_PATH =
	'/libs/mediapipe/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite';

function staticAssetUrl(path: string): string {
	return `${RuntimeConfig.staticCdnEndpoint}${path}`;
}

export interface BackgroundProcessorOptions {
	backgroundImageId?: string;
	backgroundImages?: Array<{
		id: string;
		createdAt: number;
	}>;
	mirrorCamera?: boolean;
}

export interface AppliedBackgroundProcessor {
	destroy: () => Promise<void>;
}

async function clearBackgroundProcessor(track: LocalVideoTrack): Promise<void> {
	if (!track.getProcessor()) {
		return;
	}
	await track.stopProcessor(false);
	logger.info('Cleared background processor');
}

async function applyCameraVideoProcessor(
	track: LocalVideoTrack,
	options: CameraVideoProcessorOptions,
	logLabel: string,
): Promise<AppliedBackgroundProcessor | null> {
	await clearBackgroundProcessor(track);
	const processor = createCameraVideoProcessor(options);
	await track.setProcessor(processor);
	logger.info(logLabel);
	return processor;
}

async function resolveBackgroundProcessorOptions(
	backgroundImageId: string,
	backgroundImages?: Array<{
		id: string;
		createdAt: number;
	}>,
): Promise<LiveKitBackgroundProcessorOptions | null> {
	const isNone = backgroundImageId === NONE_BACKGROUND_ID;
	const isBlur = backgroundImageId === BLUR_BACKGROUND_ID;
	if (isNone) {
		return null;
	}
	if (isBlur) {
		return {
			mode: 'background-blur',
			blurRadius: 20,
			assetPaths: {
				tasksVisionFileSet: staticAssetUrl(MEDIAPIPE_TASKS_VISION_PATH),
				modelAssetPath: staticAssetUrl(MEDIAPIPE_SEGMENTER_MODEL_PATH),
			},
		};
	}
	const backgroundImage = backgroundImages?.find((img) => img.id === backgroundImageId);
	if (!backgroundImage) {
		return null;
	}
	const imageUrl = await BackgroundImageDB.getBackgroundImageURL(backgroundImage.id);
	if (!imageUrl) {
		return null;
	}
	return {
		mode: 'virtual-background',
		imagePath: imageUrl,
		assetPaths: {
			tasksVisionFileSet: staticAssetUrl(MEDIAPIPE_TASKS_VISION_PATH),
			modelAssetPath: staticAssetUrl(MEDIAPIPE_SEGMENTER_MODEL_PATH),
		},
	};
}

export async function applyCameraMirrorProcessor(
	track: LocalVideoTrack,
	mirrorCamera = VoiceSettings.getMirrorCamera(),
) {
	try {
		if (!mirrorCamera) {
			await clearBackgroundProcessor(track);
			logger.debug('No camera mirror processor applied');
			return null;
		}
		return applyCameraVideoProcessor(track, {mirror: true}, 'Applied camera mirror');
	} catch (error) {
		logger.warn('Failed to apply camera mirror processor', error);
		return null;
	}
}

export async function applyBackgroundProcessor(
	track: LocalVideoTrack,
	options?: BackgroundProcessorOptions,
): Promise<AppliedBackgroundProcessor | null> {
	try {
		const voiceSettings = VoiceSettings;
		const backgroundImageId = options?.backgroundImageId ?? voiceSettings.backgroundImageId;
		const backgroundImages = options?.backgroundImages ?? voiceSettings.backgroundImages;
		const mirrorCamera = options?.mirrorCamera ?? voiceSettings.getMirrorCamera();
		const backgroundOptions = await resolveBackgroundProcessorOptions(backgroundImageId, backgroundImages);
		if (!backgroundOptions && !mirrorCamera) {
			await clearBackgroundProcessor(track);
			logger.debug('No camera video processor applied');
			return null;
		}
		try {
			const processor = await applyCameraVideoProcessor(
				track,
				{backgroundOptions: backgroundOptions ?? undefined, mirror: mirrorCamera},
				backgroundOptions && mirrorCamera
					? 'Applied camera background and mirror'
					: backgroundOptions
						? 'Applied camera background'
						: 'Applied camera mirror',
			);
			return processor;
		} catch (processorError) {
			if (backgroundOptions && mirrorCamera) {
				logger.warn('Failed to apply camera background; falling back to mirror only', processorError);
				return applyCameraVideoProcessor(track, {mirror: true}, 'Applied camera mirror');
			}
			throw processorError;
		}
	} catch (error) {
		logger.warn('Failed to apply camera video processor', error);
		return null;
	}
}

export function getMediaPipeAssetPaths() {
	return {
		tasksVisionFileSet: staticAssetUrl(MEDIAPIPE_TASKS_VISION_PATH),
		modelAssetPath: staticAssetUrl(MEDIAPIPE_SEGMENTER_MODEL_PATH),
	};
}
